"use client";

import { useTurnkey } from "@turnkey/sdk-react";
import { server } from "@turnkey/sdk-server";
import { Loader2, Mail } from "lucide-react";
import { useRouter } from "next/navigation";
import { createRef, useCallback, useEffect, useRef, useState } from "react";
import { FcGoogle } from "react-icons/fc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/contexts/language-context";
import { logToServer } from "@/lib/serverUtils";
import {
    checkGoogleAuthCode,
    getGoogleOAuthUrl,
    signInWithGoogle,
} from "@/server/auth/google";
import { emailOtpAuth } from "@/server/auth/login";
import { loginWithOTP } from "@/server/auth/verifyOTP";

const REQUIRED_LEN = 9; // for Email OTP

export function LoginCard() {
    const router = useRouter();
    const { indexedDbClient, turnkey } = useTurnkey();
    const { t } = useLanguage();
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [otpSent, setOtpSent] = useState<string>("");
    const [otp, setOtp] = useState("");
    const [tkUser, setTkUser] = useState<{
        subOrgId: string;
        userId: string;
    } | null>(null);
    // const [authenticatedUser, setAuthenticatedUser] = useState<{
    //     email: string;
    //     name?: string;
    // } | null>(null);
    const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(
        null,
    );
    const [countdown, setCountdown] = useState(45);

    // Reset public key when indexedDbClient is mounted
    useEffect(() => {
        const resetPublicKey = async () => {
            if (indexedDbClient) {
                await logToServer({
                    level: "info",
                    message: "Resetting public key on component mount",
                });
                await indexedDbClient.resetKeyPair();
            }
        };
        resetPublicKey();
    }, [indexedDbClient]);

    const inputRefs = useRef(
        Array(6)
            .fill(null)
            .map(() => createRef<HTMLInputElement>()),
    );

    // Focus first OTP input when otpSent changes
    useEffect(() => {
        if (otpSent) {
            // Small delay to ensure component is mounted
            const timer = setTimeout(() => {
                inputRefs.current[0].current?.focus();
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [otpSent]);

    // Cleanup interval on component unmount
    useEffect(() => {
        return () => {
            if (pollingInterval) {
                logToServer({
                    level: "info",
                    message: "Cleaning up polling interval",
                });
                clearInterval(pollingInterval);
            }
        };
    }, [pollingInterval]);

    // Add this useEffect after the other useEffects
    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (otpSent && countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        }
        return () => {
            if (timer) {
                clearInterval(timer);
            }
        };
    }, [otpSent, countdown]);

    const stopPolling = (interval: NodeJS.Timeout) => {
        logToServer({
            level: "info",
            message: "Stopping polling",
        });
        clearInterval(interval);
        setPollingInterval(null);
    };

    const handleAuthError = useCallback(
        async (error: unknown, context: string) => {
            const sessionId = (await indexedDbClient?.getPublicKey()) || "unknown";
            await logToServer({
                level: "error",
                message: `[Session: ${sessionId}] Error in ${context}`,
                data: error instanceof Error ? error.message : String(error),
            });
            toast.error(`Error in ${context}`, {
                description: error instanceof Error ? error.message : String(error),
            });
            setIsLoading(false);
        },
        [indexedDbClient],
    );

    // Handle Google OAuth login
    const handleGoogleLogin = async () => {
        setIsLoading(true);
        try {
            const sessionId = await indexedDbClient?.getPublicKey();
            if (!sessionId) {
                throw new Error("No public key available");
            }

            await logToServer({
                level: "info",
                message: `[Session: ${sessionId}] Starting Google OAuth flow`,
            });

            // Get Google OAuth URL from our API
            const url = await getGoogleOAuthUrl(sessionId);
            await logToServer({
                level: "info",
                message: `[Session: ${sessionId}] Opening Google OAuth URL in new tab`,
            });
            window.open(url, "_blank");

            // Set up polling for auth code
            const interval = setInterval(async () => {
                try {
                    await logToServer({
                        level: "info",
                        message: "Checking for auth code",
                    });
                    const response = await checkGoogleAuthCode(sessionId);

                    if (response.status === "error") {
                        await logToServer({
                            level: "error",
                            message: "Google authentication failed",
                        });
                        setIsLoading(false);
                        stopPolling(interval);
                        return;
                    }

                    if (response.status === "success" && response.code) {
                        await logToServer({
                            level: "info",
                            message: "Auth code received, completing authentication",
                        });
                        await signInWithGoogle({
                            pubId: sessionId,
                            code: response.code,
                        });
                        await logToServer({
                            level: "info",
                            message: "Google authentication successful",
                        });
                        setIsLoading(false);
                        stopPolling(interval);
                    }
                } catch (error) {
                    await handleAuthError(error, "auth code check");
                    stopPolling(interval);
                }
            }, 2000); // Check every 2 seconds

            setPollingInterval(interval);
        } catch (error) {
            await handleAuthError(error, "Google login initialization");
        }
    };

    const handleSendOTP = async () => {
        setIsLoading(true);
        try {
            const sessionId = await indexedDbClient?.getPublicKey();
            if (!sessionId) {
                throw new Error("No public key available");
            }

            await logToServer({
                level: "info",
                message: `[Session: ${sessionId}] Sending OTP to email`,
                data: { email },
            });
            const otpAuthRequest = await emailOtpAuth({
                email,
                targetPublicKey: sessionId,
            });
            await logToServer({
                level: "info",
                message: "OTP sent successfully",
            });
            setOtpSent(otpAuthRequest?.otpId || "");
            setTkUser({
                subOrgId: otpAuthRequest?.suborgId || "",
                userId: otpAuthRequest?.userId || "",
            });
            setIsLoading(false);
            setCountdown(45);
        } catch (error) {
            await handleAuthError(error, "OTP sending");
        }
    };

    const handleResendOTP = async () => {
        setCountdown(45);
        await turnkey?.logout();
        await indexedDbClient?.resetKeyPair();
        await handleSendOTP();
    };

    const handleVerifyOTP = useCallback(
        async (otpToVerify: string) => {
            if (!indexedDbClient) {
                toast.error(t("auth.login.email.errorInvalidClient"), {
                    description: t("auth.login.email.loginNotAvailable"),
                });
                setIsLoading(false);
                return;
            }
            const idbPublicKey = await indexedDbClient.getPublicKey();
            if (!idbPublicKey) {
                toast.error(t("auth.login.email.errorInvalidClient"), {
                    description: t("auth.login.email.pkNotAvailable"),
                });
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            if (
                !otpSent ||
                !tkUser?.subOrgId ||
                !tkUser?.userId ||
                otpToVerify.length < REQUIRED_LEN
            ) {
                toast.error(t("auth.login.email.errorOTPVerification"), {
                    description: t("auth.login.email.missingRequiredData"),
                });
                await logToServer({
                    level: "error",
                    message: "Missing required data for OTP verification",
                });
                setIsLoading(false);
                return;
            }

            try {
                await logToServer({
                    level: "info",
                    message: "Verifying OTP",
                });
                // I need to move all the turnkey logic here and then only keep the internal login logic in the server
                const verifyResponse = await server.verifyOtp({
                    otpId: otpSent,
                    otpCode: otpToVerify,
                    sessionLengthSeconds: 60 * 60, // 60 min
                });

                if (!verifyResponse || !verifyResponse.verificationToken) {
                    toast.error(t("auth.login.email.errorOTPVerification"), {
                        description: t("auth.login.email.errorOTPVerificationDescription"),
                    });
                    setOtp("");
                    setIsLoading(false);
                    return;
                }
                const loginSession = await server.otpLogin({
                    suborgID: tkUser.subOrgId,
                    verificationToken: verifyResponse.verificationToken,
                    publicKey: idbPublicKey,
                    sessionLengthSeconds: 60 * 60, // 60 min
                });
                if (!loginSession || !loginSession.session) {
                    toast.error(t("auth.login.email.errorLoggingIn"), {
                        description: t("auth.login.email.errorOTPVerificationDescription2"),
                    });
                    setOtp("");
                    setIsLoading(false);
                    return;
                }

                const result = await loginWithOTP({
                    suborgId: tkUser.subOrgId,
                    contact: email,
                });
                if (!result.success || !result.user) {
                    toast.error(t("auth.login.email.invalidOTP"), {
                        description: t("auth.login.email.userNotFound"),
                    });
                    setOtp("");
                    setIsLoading(false);
                    return;
                }

                toast.success(t("auth.login.email.otpVerified"), {
                    description: t("auth.login.email.loggingIn"),
                });

                await logToServer({
                    level: "info",
                    message: "OTP verified, logging in with session",
                    data: "OK",
                });
                await indexedDbClient.loginWithSession(loginSession.session);

                // Clear OTP input on successful verification
                setOtp("");

                // Redirect to dashboard or appropriate page based on onboarding status
                if (result.user.onboardingStep > 2 || result.user.profileCompleted) {
                    toast.success(t("auth.login.email.loggedIn"), {
                        description: t("auth.login.email.redirectingToDashboard"),
                    });
                    router.push("/dashboard");
                } else {
                    toast.success(t("auth.login.email.loggedIn"), {
                        description: t("auth.login.email.redirectingToOnboarding"),
                    });
                    router.push("/onboarding");
                }
                setIsLoading(false);
            } catch (error) {
                toast.error(t("auth.login.email.errorVerifyingOTP"), {
                    description: t("auth.login.email.errorVerifyingOTP"),
                });
                await handleAuthError(error, "OTP verification");
                setIsLoading(false);
            }
        },
        [indexedDbClient, otpSent, tkUser, email, router, t, handleAuthError],
    );

    const handlePasskeyLogin = () => {
        setIsLoading(true);
        // Would trigger WebAuthn flow in a real app
        setTimeout(() => {
            // Simulate successful login
            // setAuthenticatedUser({
            //     email: "passkey@example.com",
            //     name: "Passkey User",
            // });
            setIsLoading(false);
        }, 1000);
    };

    const handleWalletConnect = () => {
        setIsLoading(true);
        // Would trigger WalletConnect flow in a real app
        setTimeout(() => {
            // Simulate successful login
            // setAuthenticatedUser({
            //     email: "wallet@example.com",
            //     name: "Wallet User",
            // });
            setIsLoading(false);
        }, 1000);
    };

    const handleBackToLogin = () => {
        // setAuthenticatedUser(null);
        setOtpSent("");
        setIsLoading(false);
        setOtp("");
    };

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle className="text-2xl text-center">
                    {t("auth.login.title")}
                </CardTitle>
                <CardDescription className="text-center">
                    {t("auth.login.description")}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <Tabs defaultValue="email" className="w-full">
                    <TabsList
                        className="flex flex-row items-center justify-center gap-4"
                    // @note TEMPORARY REMOVE TABS THAT ARE NOT READY YET
                    // className="grid grid-cols-4 w-full"
                    >
                        {/* <TabsTrigger
                            value="google"
                            className="flex flex-col items-center gap-1 py-2"
                            disabled
                        >
                            <FcGoogle className="h-5 w-5" />
                            <span className="text-xs">Google</span>
                        </TabsTrigger> */}
                        <TabsTrigger
                            value="email"
                            className="flex flex-col items-center gap-1 py-2"
                        >
                            <Mail className="h-5 w-5" />
                            <span className="text-xs">{t("auth.email")}</span>
                        </TabsTrigger>
                        {/* <TabsTrigger
                            value="passkey"
                            className="flex flex-col items-center gap-1 py-2"
                            disabled
                        >
                            <Key className="h-5 w-5" />
                            <span className="text-xs">Passkey</span>
                        </TabsTrigger> */}
                        {/* <TabsTrigger
                            value="wallet"
                            className="flex flex-col items-center gap-1 py-2"
                            disabled
                        >
                            <Wallet className="h-5 w-5" />
                            <span className="text-xs">Wallet</span>
                        </TabsTrigger> */}
                    </TabsList>

                    <TabsContent value="google" className="mt-6 space-y-4">
                        <div className="text-center text-sm">
                            {t("auth.login.google.description")}
                        </div>
                        <Button
                            onClick={handleGoogleLogin}
                            className="w-full flex items-center justify-center gap-2"
                            disabled={isLoading}
                        >
                            <FcGoogle className="h-5 w-5" />
                            {t("auth.login.google.continue")}
                        </Button>
                    </TabsContent>

                    <TabsContent value="email" className="mt-4 space-y-4">
                        {!otpSent ? (
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="dark:text-offwhite">
                                        {t("auth.login.email.label")}
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder={t("auth.login.email.placeholder")}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && email && !isLoading) {
                                                handleSendOTP();
                                            }
                                        }}
                                    />
                                </div>
                                <Button
                                    onClick={handleSendOTP}
                                    className="w-full"
                                    disabled={isLoading || !email}
                                >
                                    {isLoading ? (
                                        <Loader2 className="animate-spin" />
                                    ) : (
                                        t("auth.login.email.sendCode")
                                    )}
                                </Button>
                            </>
                        ) : (
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="otp" className="dark:text-offwhite">
                                        {t("auth.login.email.enterCode")}
                                    </Label>
                                    <Input
                                        id="otp"
                                        type="text"
                                        className="w-full text-center text-lg"
                                        value={otp}
                                        maxLength={9}
                                        disabled={isLoading}
                                        onChange={(e) => {
                                            const newOtp = e.target.value;
                                            setOtp(newOtp);
                                            if (newOtp.length === REQUIRED_LEN) {
                                                handleVerifyOTP(newOtp);
                                            }
                                        }}
                                        onFocus={(e) => e.target.select()}
                                        autoComplete="one-time-code"
                                        placeholder={t("auth.login.email.enterCodePlaceholder")}
                                    />
                                    <div className="text-sm text-center mt-2 dark:text-offwhite/80">
                                        {t("auth.login.email.codeSent").replace("{email}", email)}
                                    </div>
                                </div>
                                <div className="flex flex-col space-y-2">
                                    <Button
                                        onClick={() => handleVerifyOTP(otp)}
                                        className="w-full"
                                        disabled={isLoading || otp.length < REQUIRED_LEN}
                                    >
                                        {isLoading ? (
                                            <Loader2 className="animate-spin" />
                                        ) : (
                                            t("auth.login.email.verify")
                                        )}
                                    </Button>

                                    <>
                                        {countdown > 0 ? (
                                            <div className="text-center text-sm text-muted-foreground">
                                                {t("auth.login.email.tryAgainIn").replace(
                                                    "{time}",
                                                    countdown.toString(),
                                                )}
                                            </div>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="outline"
                                                    onClick={handleResendOTP}
                                                    className="w-full text-sm"
                                                    disabled={isLoading}
                                                >
                                                    {isLoading ? (
                                                        <Loader2 className="animate-spin" />
                                                    ) : (
                                                        t("auth.login.email.resendCode")
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    onClick={() => handleBackToLogin()}
                                                    className="w-full text-sm"
                                                    disabled={isLoading || countdown > 0}
                                                >
                                                    {t("auth.login.email.useDifferentEmail")}
                                                </Button>
                                            </>
                                        )}
                                    </>
                                </div>
                            </>
                        )}
                    </TabsContent>

                    <TabsContent value="passkey" className="mt-6 space-y-4">
                        <div className="text-center text-sm">
                            {t("auth.login.passkey.description")}
                        </div>
                        <Button
                            onClick={handlePasskeyLogin}
                            className="w-full"
                            disabled={isLoading}
                        >
                            {t("auth.login.passkey.continue")}
                        </Button>
                    </TabsContent>

                    <TabsContent value="wallet" className="mt-6 space-y-4">
                        <div className="text-center text-sm">
                            {t("auth.login.wallet.description")}
                        </div>
                        <Button
                            onClick={handleWalletConnect}
                            className="w-full"
                            disabled={isLoading}
                        >
                            {t("auth.login.wallet.connect")}
                        </Button>
                    </TabsContent>
                </Tabs>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
                <Separator />
                <div className="text-center text-sm whitespace-pre-wrap">
                    {t("auth.login.noAccount")}
                    {"\n"}
                    <span className="font-bold">{t("auth.login.autoCreate")}</span>
                </div>
            </CardFooter>
        </Card>
    );
}
